import type { LabelProduct } from '../api/client.js';

/**
 * The Labeling page's list of scanned products, as pure functions over an
 * immutable array: add, change a quantity, remove, clear, and the round trip
 * through sessionStorage that lets a refresh keep the list.
 */

/** One row of the list: a looked-up product and how many labels to print for it. */
export interface QueueRow {
  /** Unique within the list; a product scanned twice as separate rows gets two. */
  id: string;
  product: LabelProduct;
  qty: number;
}

/** A label quantity is a whole number in this range. */
export const MIN_QTY = 1;
export const MAX_QTY = 999;

/**
 * What scanning a product that is already in the list does:
 *   - `increment`: adds one to its quantity (and the page highlights the row);
 *   - `append`: adds a second row underneath, like any other scan.
 * This one line is the whole switch.
 */
export const DUPLICATE_SCAN: 'increment' | 'append' = 'increment';

export interface AddResult {
  rows: QueueRow[];
  /** The row that was added or incremented, for the page to highlight and reveal. */
  rowId: string;
  /** True when an existing row's quantity went up instead of a row being added. */
  incremented: boolean;
}

/** A row id no other row in the list uses. */
function nextRowId(rows: readonly QueueRow[], key: string): string {
  const taken = new Set(rows.map((r) => r.id));
  for (let n = 1; ; n += 1) {
    const id = `${key}#${n}`;
    if (!taken.has(id)) return id;
  }
}

/**
 * Adds a looked-up product to the end of the list with a quantity of one — or,
 * when it is already there and {@link DUPLICATE_SCAN} is `increment`, adds one
 * to that row (stopping at {@link MAX_QTY}) and refreshes its product details.
 */
export function addProduct(
  rows: readonly QueueRow[],
  product: LabelProduct,
  mode: 'increment' | 'append' = DUPLICATE_SCAN,
): AddResult {
  const existing = mode === 'increment' ? rows.find((r) => r.product.key === product.key) : null;
  if (existing) {
    return {
      rows: rows.map((r) =>
        r.id === existing.id ? { ...r, product, qty: Math.min(r.qty + 1, MAX_QTY) } : r,
      ),
      rowId: existing.id,
      incremented: true,
    };
  }
  const id = nextRowId(rows, product.key);
  return { rows: [...rows, { id, product, qty: MIN_QTY }], rowId: id, incremented: false };
}

/**
 * A quantity from what was typed: a whole number from {@link MIN_QTY} to
 * {@link MAX_QTY}, or null for anything else (blank, a decimal, zero, 1000,
 * letters, a sign). The text is read as written, never rounded or clamped.
 */
export function parseQuantity(text: string): number | null {
  const trimmed = text.trim();
  if (!/^[0-9]{1,3}$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value >= MIN_QTY && value <= MAX_QTY ? value : null;
}

export function setQuantity(rows: readonly QueueRow[], id: string, qty: number): QueueRow[] {
  return rows.map((r) => (r.id === id ? { ...r, qty } : r));
}

export function removeRow(rows: readonly QueueRow[], id: string): QueueRow[] {
  return rows.filter((r) => r.id !== id);
}

export interface QueueTotals {
  /** Rows in the list, and how many of them can be printed. */
  rows: number;
  printableRows: number;
  /** Labels a print would produce: the quantities of the printable rows. */
  labels: number;
  /** Rows skipped by a print because their product has no FNSKU on file. */
  skippedRows: number;
}

export function queueTotals(rows: readonly QueueRow[]): QueueTotals {
  const printable = rows.filter((r) => r.product.printable && r.product.fnsku !== null);
  return {
    rows: rows.length,
    printableRows: printable.length,
    labels: printable.reduce((sum, r) => sum + r.qty, 0),
    skippedRows: rows.length - printable.length,
  };
}

// ---------------------------------------------------------------------------
// sessionStorage
// ---------------------------------------------------------------------------

/** Where the list is kept for the life of the tab. */
export const QUEUE_STORAGE_KEY = 'seller.labels.queue';

/** Bumped when the stored shape changes, so an old list is dropped, not misread. */
const STORAGE_VERSION = 1;

export function serializeQueue(rows: readonly QueueRow[]): string {
  return JSON.stringify({ version: STORAGE_VERSION, rows });
}

/**
 * The stored list, or an empty one for anything that is not a list this version
 * wrote: nothing stored, malformed JSON, another version, or a row missing what
 * a print needs. One bad row drops only that row.
 */
export function parseQueue(stored: string | null): QueueRow[] {
  if (!stored) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(stored);
  } catch {
    return [];
  }
  if (typeof parsed !== 'object' || parsed === null) return [];
  const { version, rows } = parsed as { version?: unknown; rows?: unknown };
  if (version !== STORAGE_VERSION || !Array.isArray(rows)) return [];
  const out: QueueRow[] = [];
  const seen = new Set<string>();
  for (const row of rows as unknown[]) {
    if (typeof row !== 'object' || row === null) continue;
    const { id, product, qty } = row as { id?: unknown; product?: unknown; qty?: unknown };
    if (typeof id !== 'string' || seen.has(id)) continue;
    if (typeof qty !== 'number' || !Number.isInteger(qty) || qty < MIN_QTY || qty > MAX_QTY) {
      continue;
    }
    if (!isLabelProduct(product)) continue;
    seen.add(id);
    out.push({ id, product, qty });
  }
  return out;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isStringOrNull(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

const LIVE_REASONS: readonly unknown[] = ['only_sku', 'others_superseded', 'ranked'];
const MATCH_SOURCES: readonly unknown[] = ['asin', 'fnsku', 'sku', 'upc', 'booking', 'catalog'];
/** Every `LabelTaxonomy` field; each is a string or null once it has been through JSON. */
const TAXONOMY_FIELDS = [
  'category',
  'series',
  'model',
  'color',
  'sizeLabel',
  'gauge',
  'gaugeMm',
  'putUp',
  'putUpLength',
] as const;

/**
 * Whether a stored value has the whole `LabelProduct` shape the page reads, so
 * a row written by an older build or edited by hand is dropped instead of
 * crashing the page on every load. Enumerated values (the category, the gauge)
 * are checked as strings only: the page shows them as text.
 */
function isLabelProduct(value: unknown): value is LabelProduct {
  if (!isRecord(value)) return false;
  const p = value;
  const taxonomyOk =
    p.taxonomy === null ||
    (isRecord(p.taxonomy) &&
      TAXONOMY_FIELDS.every((f) => isStringOrNull((p.taxonomy as Record<string, unknown>)[f])));
  return (
    typeof p.key === 'string' &&
    typeof p.sellerSku === 'string' &&
    typeof p.skuId === 'number' &&
    Number.isInteger(p.skuId) &&
    typeof p.printable === 'boolean' &&
    isStringOrNull(p.fnsku) &&
    isStringOrNull(p.asin) &&
    isStringOrNull(p.title) &&
    isStringOrNull(p.status) &&
    isStringOrNull(p.note) &&
    taxonomyOk &&
    typeof p.skuCount === 'number' &&
    Number.isInteger(p.skuCount) &&
    LIVE_REASONS.includes(p.liveReason) &&
    MATCH_SOURCES.includes(p.matchedBy) &&
    Array.isArray(p.otherSkus) &&
    p.otherSkus.every(
      (o: unknown) =>
        isRecord(o) &&
        typeof o.sellerSku === 'string' &&
        isStringOrNull(o.status) &&
        isStringOrNull(o.fnsku),
    )
  );
}
